import { ActivityHistory, AuditType } from '@user/features/Activity/state'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'
import format from 'date-fns/format'
import { useMemo } from 'react'
import {
  ActivityHistoryType,
  AuditTypeName,
  printAndSign,
  uploadedByActionVerbiage,
} from '@user/features/Activity/ActivityHistory/constants'
import { ENTITY_TYPES } from '@sales-center/common'
import { useActivitiesContext } from '@user/features/Activity/context'

type Props = {
  historyElement: ActivityHistory
}

export function useData(props: Props) {
  const [{ history }] = useActivitiesContext()

  const {
    historyElement: {
      auditTime,
      isClosingActivity,
      isCustomActivity,
      isStatusUpdate,
      description,
      header,
      name,
      statusChange,
      assignmentUpdateDetails,
      activityHistoryType,
      auditTypeName,
      entityType,
      auditType,
      agreementUploadedBy,
    },
  } = props

  const { formatDate } = useLocalizationDateFormat()

  const dateFormatted = useMemo(
    () => (auditTime ? formatDate(auditTime) : ''),
    [auditTime, formatDate],
  )
  const timeFormatted = useMemo(
    () => (auditTime ? format(auditTime, 'h:mm aa') : ''),
    [auditTime],
  )

  const isAssignmentUpdate = useMemo(
    () => !!assignmentUpdateDetails,
    [assignmentUpdateDetails],
  )

  const title = useMemo(() => {
    if (activityHistoryType === ActivityHistoryType.KnockStatusUpdate) {
      return 'Knock Status Update'
    }
    if (isClosingActivity || isCustomActivity) {
      return `${name}${header && header.length > 0 ? ` - ${header}` : ``}`
    }
    if (isStatusUpdate) {
      return `${name}`
    }
    return description || header || statusChange
  }, [
    description,
    header,
    isClosingActivity,
    isCustomActivity,
    isStatusUpdate,
    name,
    statusChange,
    activityHistoryType,
  ])

  const subTitle = useMemo(() => {
    if (activityHistoryType === ActivityHistoryType.KnockStatusUpdate) {
      if (auditType === AuditType.Update) {
        return `New status - ${statusChange || 'None'}`
      }
      return statusChange || 'None'
    }

    if (assignmentUpdateDetails && assignmentUpdateDetails.newOwner) {
      if (assignmentUpdateDetails?.newOwnerIsActive === false) {
        return `Owner - ${assignmentUpdateDetails.newOwner} (Inactive)`
      }
      return `Owner - ${assignmentUpdateDetails.newOwner}`
    }

    if (
      activityHistoryType == ActivityHistoryType.CustomActivity &&
      auditTypeName == AuditTypeName.Delete
    ) {
      return 'Deleted'
    }

    if (isClosingActivity) {
      return description
    }
    if (isCustomActivity) {
      return statusChange
    }
    if (isStatusUpdate) {
      return statusChange
    }
    return description || header || statusChange
  }, [
    description,
    header,
    isClosingActivity,
    isCustomActivity,
    isStatusUpdate,
    statusChange,
    assignmentUpdateDetails,
    activityHistoryType,
    auditTypeName,
    auditType,
  ])

  const subTitle2 = useMemo(() => {
    if (
      activityHistoryType === ActivityHistoryType.KnockStatusUpdate &&
      auditType === AuditType.Update
    ) {
      return `Previous status - ${header || 'None'}`
    }
    if (assignmentUpdateDetails?.newOwner) {
      return `Previous Owner - ${
        assignmentUpdateDetails?.isFirstAssignment
          ? `(${assignmentUpdateDetails?.previousOwner})`
          : assignmentUpdateDetails?.previousOwnerIsActive === false
            ? `${assignmentUpdateDetails.previousOwner} (Inactive)`
            : assignmentUpdateDetails?.previousOwner
      }`
    } else {
      return null
    }
  }, [assignmentUpdateDetails, activityHistoryType, auditType, header])

  const updatedBy = useMemo(() => {
    if (statusChange == 'Closed/Won' && header != null) {
      return header
    }

    // Special case for Agreement "Print and Sign" action
    if (
      activityHistoryType === ActivityHistoryType.Agreement &&
      description === printAndSign
    ) {
      return agreementUploadedBy
    }

    const assignmentUpdatedBy =
      assignmentUpdateDetails?.assignmentUpdatedByIsActive === false
        ? `${assignmentUpdateDetails.assignmentUpdatedBy} (Inactive)`
        : assignmentUpdateDetails?.assignmentUpdatedBy

    return assignmentUpdateDetails?.assignmentUpdatedBy
      ? `${assignmentUpdatedBy} ${
          assignmentUpdateDetails.automatedAssignmentRule
            ? ' Assignment Rule'
            : ''
        }`
      : null
  }, [
    statusChange,
    header,
    activityHistoryType,
    description,
    assignmentUpdateDetails?.assignmentUpdatedByIsActive,
    assignmentUpdateDetails?.assignmentUpdatedBy,
    assignmentUpdateDetails?.automatedAssignmentRule,
    agreementUploadedBy,
  ])

  const actionVerbiage = useMemo(() => {
    // Special case for Knock Status Update
    if (activityHistoryType === ActivityHistoryType.KnockStatusUpdate) {
      return auditType === AuditType.Create ? 'Added by' : 'Updated by'
    }

    // Special case for Agreement "Print and Sign" action
    if (
      activityHistoryType === ActivityHistoryType.Agreement &&
      description === printAndSign
    ) {
      return uploadedByActionVerbiage
    }

    // Find the chronologically first status update based on auditTime
    const statusUpdates = history.filter((item) => item.isStatusUpdate)
    const firstStatusUpdate =
      statusUpdates.length > 0
        ? statusUpdates.reduce((earliest, current) =>
            current.auditTime < earliest.auditTime ? current : earliest,
          )
        : null
    const isFirstStatusUpdate = firstStatusUpdate === props.historyElement

    // For the first Funnel Update entry, use specific verbiage
    if (
      isStatusUpdate &&
      auditType === AuditType.Create &&
      isFirstStatusUpdate
    ) {
      if (entityType === ENTITY_TYPES.Lead) {
        return 'Lead Added by'
      } else if (entityType === ENTITY_TYPES.Opportunity) {
        return 'Opportunity Created by'
      }
    }
    // For subsequent updates, keep "Updated by"
    return 'Updated by'
  }, [
    activityHistoryType,
    auditType,
    description,
    history,
    props.historyElement,
    isStatusUpdate,
    entityType,
  ])

  return {
    dateFormatted,
    subTitle,
    subTitle2,
    timeFormatted,
    title,
    updatedBy,
    isAssignmentUpdate,
    actionVerbiage,
  } as const
}
